import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { calendarAPI } from '../../../api/client';
import {
  LinkIcon, ArrowPathIcon, PhoneArrowUpRightIcon,
  CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon,
  ArrowTopRightOnSquareIcon, TrashIcon, SpeakerWaveIcon,
  MapPinIcon, ClockIcon, BoltIcon,
} from '@heroicons/react/24/outline';

// Auto-sync interval options — must match backend ALLOWED_SYNC_INTERVALS.
const SYNC_INTERVALS = [
  { value: 5,    label: 'Every 5 minutes' },
  { value: 10,   label: 'Every 10 minutes' },
  { value: 15,   label: 'Every 15 minutes' },
  { value: 30,   label: 'Every 30 minutes' },
  { value: 60,   label: 'Every hour' },
  { value: 120,  label: 'Every 2 hours' },
  { value: 1440, label: 'Once a day' },
];

// Human "in 4m 12s" style countdown to an ISO timestamp; '' if past/absent.
function countdownTo(iso) {
  if (!iso) return '';
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'any moment now';
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `in ${h}h ${m}m`;
  }
  if (mins > 0) return `in ${mins}m ${secs}s`;
  return `in ${secs}s`;
}

export default function CalendarSection() {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [phone, setPhone] = useState('');
  const [defaultMinutes, setDefaultMinutes] = useState(30);
  const [assistantId, setAssistantId] = useState('');
  const [savingSync, setSavingSync] = useState(false);
  const [, setTick] = useState(0);   // re-render every second for the countdown

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await calendarAPI.getConnection();
      setConn(res.data);
      setPhone(res.data.phone_number || '');
      setDefaultMinutes(res.data.default_reminder_minutes ?? 30);
      setAssistantId(res.data.vapi_assistant_id || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load calendar settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tick once a second so the "next sync" countdown stays live.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const toggleAutoSync = async () => {
    if (!conn) return;
    const next = !conn.auto_sync_enabled;
    setSavingSync(true);
    try {
      const res = await calendarAPI.updateSettings({ auto_sync_enabled: next });
      setConn(res.data);
      toast.success(next ? 'Auto sync enabled' : 'Auto sync disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update auto sync');
    } finally {
      setSavingSync(false);
    }
  };

  const changeInterval = async (minutes) => {
    setSavingSync(true);
    try {
      const res = await calendarAPI.updateSettings({ auto_sync_interval_minutes: parseInt(minutes) });
      setConn(res.data);
      toast.success('Sync interval updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update interval');
    } finally {
      setSavingSync(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await calendarAPI.getGoogleAuthUrl();
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start Google OAuth flow.');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Synced events will be removed.')) return;
    try {
      const res = await calendarAPI.disconnect();
      setConn(res.data);
      toast.success('Google Calendar disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await calendarAPI.sync();
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await calendarAPI.updateSettings({
        phone_number: phone,
        default_reminder_minutes: parseInt(defaultMinutes) || 0,
        vapi_assistant_id: assistantId,
      });
      setConn(res.data);
      toast.success('Reminder settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleReminders = async () => {
    if (!conn) return;
    const next = !conn.reminders_enabled;
    try {
      const res = await calendarAPI.updateSettings({ reminders_enabled: next });
      setConn(res.data);
      toast.success(next ? 'Call reminders enabled' : 'Call reminders disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const testCallNow = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await calendarAPI.testNextReminder();
      setTestResult(res.data);
      toast.success(`Calling ${conn?.phone_number} now…`);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 404) setTestResult('no_events');
      else toast.error(detail || 'Could not place test call');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const callReady = conn?.is_connected && conn?.vapi_configured && conn?.twilio_configured
    && conn?.assistant_ready && !!conn?.phone_number && conn?.reminders_enabled;

  return (
    <div className="space-y-6">
      {/* Connection card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl bg-red-50">📅</div>
            <div>
              <p className="font-semibold text-slate-800">Google Calendar</p>
              <p className="text-sm text-slate-500">
                {conn?.is_connected ? (conn.google_email || 'Connected') : 'Not connected'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  conn?.is_connected ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {conn?.is_connected ? '● Connected' : '○ Not connected'}
                </span>
                {conn?.last_synced && (
                  <span className="text-xs text-slate-400">
                    Last synced: {new Date(conn.last_synced).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {conn?.is_connected ? (
              <>
                <button
                  onClick={syncNow}
                  disabled={syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing…' : 'Sync Now'}
                </button>
                <button
                  onClick={disconnect}
                  className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                  title="Disconnect"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={connect}
                disabled={connecting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 disabled:opacity-50 transition-colors"
              >
                <LinkIcon className="w-4 h-4" /> {connecting ? 'Redirecting…' : 'Connect'}
              </button>
            )}
          </div>
        </div>

        {!conn?.is_connected && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100 mt-4">
            <InformationCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              Connecting grants <strong>read-only</strong> access to Google Calendar
              (<code>calendar.readonly</code>). Once connected, upcoming events sync
              automatically on the schedule you choose below.
            </p>
          </div>
        )}
      </div>

      {/* Auto Sync card */}
      {conn?.is_connected && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-5 flex items-center justify-between gap-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-50">
                <BoltIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Auto Sync</p>
                <p className="text-sm text-slate-500">Automatically pull upcoming events in the background</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-sm text-slate-600 font-medium select-none">
                {conn?.auto_sync_enabled ? 'On' : 'Off'}
              </span>
              <button
                onClick={toggleAutoSync}
                disabled={savingSync}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 disabled:opacity-50 ${
                  conn?.auto_sync_enabled ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  conn?.auto_sync_enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sync Frequency</label>
              <select
                value={conn?.auto_sync_interval_minutes ?? 15}
                onChange={(e) => changeInterval(e.target.value)}
                disabled={savingSync || !conn?.auto_sync_enabled}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 disabled:bg-slate-50"
              >
                {SYNC_INTERVALS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <ClockIcon className="w-3.5 h-3.5 text-slate-400" />
                Last synced:{' '}
                <span className="text-slate-700 font-medium">
                  {conn?.last_synced ? new Date(conn.last_synced).toLocaleString() : 'never'}
                </span>
              </span>
              {conn?.auto_sync_enabled && (
                <span className="flex items-center gap-1.5">
                  <ArrowPathIcon className="w-3.5 h-3.5 text-slate-400" />
                  Next sync:{' '}
                  <span className="text-emerald-700 font-medium">
                    {conn?.next_sync ? countdownTo(conn.next_sync) : 'scheduling…'}
                  </span>
                </span>
              )}
            </div>

            {!conn?.auto_sync_enabled && (
              <p className="text-xs text-amber-600">
                Auto sync is off — events only refresh when you click <strong>Sync Now</strong>.
                Reminder calls rely on freshly-synced events, so keep this on for hands-free reminders.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Reminder settings card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 flex items-center justify-between gap-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-50">
              <PhoneArrowUpRightIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">Phone Call Reminders</p>
              <p className="text-sm text-slate-500">Get an automated call before your events</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-slate-600 font-medium select-none">
              {conn?.reminders_enabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              onClick={toggleReminders}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                conn?.reminders_enabled ? 'bg-indigo-500' : 'bg-slate-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                conn?.reminders_enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reminder Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 320 433 8568"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <p className="text-xs text-slate-400 mt-1">
              The number to call for reminders. Use international format, e.g. <code>+13204338568</code>.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Default Reminder Time</label>
            <select
              value={defaultMinutes}
              onChange={(e) => setDefaultMinutes(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              {[5, 10, 15, 30, 45, 60, 120].map((m) => (
                <option key={m} value={m}>{m} minutes before</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">
              Applied to events that don't have their own reminder time set.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Vapi Assistant ID <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={assistantId}
              onChange={(e) => setAssistantId(e.target.value)}
              placeholder="Falls back to the Call Monitor assistant if left blank"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <p className="text-xs text-slate-400 mt-1">
              The Vapi assistant that places reminder calls. Leave blank to reuse the one configured in Call Monitor.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>

        <div className="border-t border-slate-100 p-5">
          {callReady ? (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
              Reminders are ready — calls will be placed automatically before your events.
            </div>
          ) : (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">A few things are needed before reminders can fire:</p>
                <ul className="mt-1 space-y-0.5 text-xs text-amber-700">
                  {!conn?.is_connected && <li>• Connect your Google Calendar</li>}
                  {!conn?.phone_number && <li>• Set a reminder phone number</li>}
                  {!conn?.reminders_enabled && <li>• Enable call reminders (toggle above)</li>}
                  {!conn?.vapi_configured && <li>• VAPI_PRIVATE_KEY is missing from the server .env</li>}
                  {!conn?.twilio_configured && <li>• Twilio credentials are missing from the server .env</li>}
                  {conn?.vapi_configured && !conn?.assistant_ready && (
                    <li>• Set a Vapi Assistant ID (here or in Call Monitor)</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Test Call Now card */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-5 flex items-center gap-3 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 flex-shrink-0">
            <SpeakerWaveIcon className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-800">Test Call Now</p>
            <p className="text-sm text-slate-500">
              Places a real call using your next upcoming event's actual details
            </p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Clicking the button below will call <strong>{conn?.phone_number || 'your reminder number'}</strong> right
            now using the details of your <strong>next upcoming calendar event</strong>. The scheduled reminder for
            that event is <strong>not consumed</strong> — it will still fire automatically at the configured lead time.
          </p>

          <button
            onClick={testCallNow}
            disabled={testing || !conn?.is_connected || !conn?.phone_number || !conn?.assistant_ready || !conn?.vapi_configured || !conn?.twilio_configured}
            className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <PhoneArrowUpRightIcon className="w-4 h-4" />
            {testing ? 'Calling…' : 'Test Call Now'}
          </button>

          {testResult === 'no_events' && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">No upcoming events found to test with.</p>
                <p className="text-xs mt-0.5">Sync your calendar first — events from the next 30 days need to be imported.</p>
              </div>
            </div>
          )}

          {testResult && testResult !== 'no_events' && (
            <div className="rounded-xl border border-green-200 bg-green-50 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-green-100">
                <CheckCircleIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                <p className="text-sm font-semibold text-green-800">Call placed successfully</p>
              </div>
              <div className="px-4 py-3 space-y-2">
                <p className="text-sm font-semibold text-slate-800">{testResult.event_title}</p>
                <div className="space-y-1 text-xs text-slate-600">
                  {testResult.event_time && testResult.event_time !== '(all day)' && (
                    <p className="flex items-center gap-1.5">
                      <ClockIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      {testResult.event_time}
                    </p>
                  )}
                  {testResult.event_location && (
                    <p className="flex items-center gap-1.5">
                      <MapPinIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      {testResult.event_location}
                    </p>
                  )}
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  Vapi call ID: <code className="font-mono">{testResult.vapi_call_id || '—'}</code>
                  {' · '}status: <span className="font-medium">{testResult.status}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Google setup note */}
      <div className="flex items-start gap-2 text-xs text-slate-400">
        <ArrowTopRightOnSquareIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>
          The redirect URI <code>/api/calendar/google/callback</code> must be listed under
          "Authorized redirect URIs" for your Google OAuth client (the same client used for Gmail).
        </p>
      </div>
    </div>
  );
}
